import { useCallback } from 'react';
import { Intersect } from '@treedis/tour-loader/dist/sdk-components/SceneComponent';
import { useNotesContext } from './useNotesContext';
import { AddNoteMode } from '../enums';
import { useNotesStore } from '../stores/notes.store';

export function useAddNote() {
  const {
    showCaseTemp,
    attachToCursor,
    tagNode,
    controls,
    inputNodeComponent,
    iframe,
    normal,
    clickListenerRef,
    setNewThread,
    closeDrawer,
    userId,
    setUserId,
    setAddingWorkflow,
  } = useNotesContext();

  const { selectNote } = useNotesStore((state) => state.actions);

  const addTag = useCallback(
    async (
      cb: (
        position: { x: number; y: number; z: number },
        stemVector: { x: number; y: number; z: number },
      ) => void,
    ) => {
      if (!showCaseTemp.tag) {
        return;
      }

      attachToCursor.current = true;

      await showCaseTemp.tag.initTagEditObject({
        tagNode,
        inputNodeComponent,
        controls,
        options: {},
        userNavigationEnabled: false,
      });

      showCaseTemp.mpSDK.Pointer.intersection.subscribe((e: Intersect) => {
        const { x, y, z } = e.normal;
        if (normal.current?.isVector3) normal.current.set(x, y, z);
      });

      const clickListener = async () => {
        iframe.contentWindow.document.removeEventListener('click', clickListener);
        iframe.contentWindow.document.removeEventListener('touchend', clickListener);
        if (inputNodeComponent.current && tagNode.current) {
          inputNodeComponent.current.inputs.userNavigationEnabled = true;
          inputNodeComponent.current.inputs.eventsEnabled = false;
          inputNodeComponent.current.inputs.unfiltered = true;
          const position = tagNode.current.obj3D?.position?.clone();
          const stemVector = normal.current
            ? normal.current.clone().clampLength(0, 0.3)
            : {
                x: 0,
                y: 0.3,
                z: 0,
              };

          tagNode.current.stop();
          tagNode.current = undefined;
          inputNodeComponent.current = undefined;

          cb(position, stemVector);
        }
      };
      clickListenerRef.current = clickListener;
      iframe.contentWindow.document.addEventListener('click', clickListener, {
        once: true,
        capture: true,
      });
      iframe.contentWindow.document.addEventListener('touchend', clickListener, {
        once: true,
        capture: true,
      });
    },

    [iframe.contentWindow.document, showCaseTemp.mpSDK.Pointer.intersection, showCaseTemp.tag],
  );

  const handleAddingNote = async () => {
    closeDrawer();

    await addTag(async (position, stemVector) => {
      const threadToAdd = {
        id: `temp-id-${Math.random()}`,
        sid: `temp-sid-${Math.random()}`,
        positionX: position.x,
        positionY: position.y,
        positionZ: position.z,
        stemVectorX: stemVector.x,
        stemVectorY: stemVector.y,
        stemVectorZ: stemVector.z,
        mpTitle: false,
        tooltip: false,
        stemVisible: true,
        mpNavigation: true,
        enabled: true,
        iconUrl: `${process.env.NEXT_PUBLIC_API || 'http://localhost:5030'}/v1/public/icons/thread`,
        description: '',
      };

      const sid = await showCaseTemp.tag.add(threadToAdd);
      threadToAdd.sid = sid;
      if (showCaseTemp.tag && controls.current) {
        attachToCursor.current = false;

        showCaseTemp.tag.startTranslateControls({
          tag: threadToAdd,
          tagNode,
          inputNodeComponent,
          controls,
          cb: () => {
            const { x, y, z } = tagNode.current.obj3D.position;
            threadToAdd.positionX = x;
            threadToAdd.positionY = y;
            threadToAdd.positionZ = z;
            showCaseTemp.tag.edit(threadToAdd);
          },
        });
      }
      setNewThread(threadToAdd);
      setAddingWorkflow(null);
    });
  };

  const handleAddNote = useCallback(() => {
    if (!userId) {
      setUserId(null);
      return;
    }

    selectNote(null);
    setAddingWorkflow({ active: true, mode: AddNoteMode.Temporary });

    handleAddingNote();
  }, [userId, handleAddingNote]);

  return { handleAddNote };
}
